import { iamLog } from '@identity/application/observability';
import type { CreatePasswordDto } from '@identity/application/dtos';
import type { IAuthApiPort, ICredentialSourcePort } from '@identity/application/ports';
import { extractErrorAttributes, subjectAttributes } from '@identity/application/utils';
import type { IdentityFlow } from '@identity/domain/types';
import { createPassword } from '@identity/domain/value-objects';

/**
 * Writes the account password, checking the policy before anything leaves the front end.
 *
 * It serves both the account that has no password yet and the one replacing the password it can
 * cite. They are the same write on the same endpoint, and `iam.isChange` is what tells them apart
 * on the dashboard, so a caller that forgets the current password cannot report one as the other.
 *
 * The value object is created here and not in presentation, so a UI that skipped its own validation
 * still sends nothing. The backend remains the final authority.
 *
 * The policy is checked on the new password only. The one being replaced may well be a password that
 * does not meet the current policy, which is one of the reasons the requirement was pending.
 *
 * Neither password is logged, not whole, not truncated, not its length.
 */
export const createPasswordUseCase = (
  authApi: IAuthApiPort,
  credentials: ICredentialSourcePort,
  flow: IdentityFlow,
) => {
  const execute = async (input: CreatePasswordDto): Promise<void> => {
    const password = createPassword(input.password);
    const subject = {
      ...subjectAttributes(credentials.readUserId()),
      'iam.flow': flow,
      'iam.isChange': Boolean(input.currentPassword),
    };

    try {
      await authApi.createPassword({ password: password.value, currentPassword: input.currentPassword });

      iamLog.info('passwordChange', subject);
    } catch (error) {
      iamLog.error('passwordChange', { ...subject, ...extractErrorAttributes(error) });
      throw error;
    }
  };

  return { execute };
};
